import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Send, Clock, RefreshCw, Trash2, AlertTriangle, Sparkles, MessageSquare,
  CheckCircle2, XCircle, Hourglass, Megaphone, Bell, Sunrise, Coffee, Moon,
  HelpCircle, BarChart3, ArrowRight, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const t = localStorage.getItem('token');
  return { Authorization: `Bearer ${t}` };
};

const KIND_META = {
  pick_v2: { label: 'PICK', icon: Send, color: 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30' },
  reminder_v2: { label: 'REMINDER', icon: Bell, color: 'bg-sky-600/20 text-sky-300 border-sky-500/30' },
  daily_balance_v2: { label: 'BALANCE', icon: BarChart3, color: 'bg-amber-600/20 text-amber-300 border-amber-500/30' },
  greeting_v2: { label: 'SALUDO', icon: Sunrise, color: 'bg-indigo-600/20 text-indigo-300 border-indigo-500/30' },
  filler_question_v2: { label: 'PREGUNTA', icon: HelpCircle, color: 'bg-pink-600/20 text-pink-300 border-pink-500/30' },
  community_text_v2: { label: 'COMUNIDAD', icon: MessageSquare, color: 'bg-fuchsia-600/20 text-fuchsia-300 border-fuchsia-500/30' },
};

const STATUS_META = {
  pending: { label: 'Pendiente', icon: Hourglass, color: 'text-amber-300' },
  published: { label: 'Enviado', icon: CheckCircle2, color: 'text-emerald-400' },
  failed: { label: 'Fallido', icon: XCircle, color: 'text-red-400' },
  skipped: { label: 'Omitido', icon: XCircle, color: 'text-zinc-500' },
};

function minutesUntil(iso) {
  try {
    const when = new Date(iso).getTime();
    const now = Date.now();
    return Math.round((when - now) / 60000);
  } catch {
    return null;
  }
}

function formatCountdown(mins) {
  if (mins === null || mins === undefined) return '—';
  if (mins < 0) {
    const ago = Math.abs(mins);
    if (ago < 60) return `hace ${ago} min`;
    const h = Math.floor(ago / 60);
    return `hace ${h}h ${ago % 60}m`;
  }
  if (mins < 60) return `en ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `en ${h}h ${m}m`;
}

function formatISOHuman(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      weekday: 'short', day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function pickLabel(pickInfo) {
  if (!pickInfo) return '';
  const home = (pickInfo.home_team || '').slice(0, 14);
  const away = (pickInfo.away_team || '').slice(0, 14);
  const pred = (pickInfo.prediction || '').slice(0, 32);
  return `${home} vs ${away} · ${pred}${pickInfo.odds ? ` @${pickInfo.odds}` : ''}`;
}

function QueueEntry({ entry, onCancel }) {
  const meta = KIND_META[entry.kind] || { label: entry.kind, icon: Megaphone, color: 'bg-zinc-600/20 text-zinc-300' };
  const statusMeta = STATUS_META[entry.status] || { label: entry.status, icon: Hourglass, color: 'text-zinc-400' };
  const KindIcon = meta.icon;
  const StatusIcon = statusMeta.icon;
  const mins = minutesUntil(entry.scheduled_for);
  const isPending = entry.status === 'pending';
  const isOverdue = isPending && mins < -2;

  return (
    <Card
      data-testid={`queue-entry-${entry.id}`}
      className={`bg-zinc-950/60 border ${isOverdue ? 'border-red-500/40' : 'border-white/5'} hover:border-white/10 transition-colors`}
    >
      <CardContent className="py-3 px-4">
        <div className="flex items-start gap-3">
          <div className={`rounded-lg p-2 border ${meta.color} shrink-0`}>
            <KindIcon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${meta.color} border text-[10px] tracking-wide`}>{meta.label}</Badge>
              <Badge variant="outline" className="text-[10px] uppercase border-white/10 text-zinc-400">
                {entry.channel_target || '—'}
              </Badge>
              <div className={`flex items-center gap-1 text-xs ${statusMeta.color}`}>
                <StatusIcon className="w-3.5 h-3.5" />
                {statusMeta.label}
              </div>
              {isPending && (
                <Badge
                  variant="outline"
                  className={`text-[11px] font-mono ${isOverdue ? 'border-red-500/40 text-red-300' : 'border-white/10 text-zinc-200'}`}
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {formatCountdown(mins)}
                </Badge>
              )}
              {entry.streak_adjusted && (
                <Badge variant="outline" className="text-[10px] border-fuchsia-500/40 text-fuchsia-300">
                  streak {entry.streak_adjusted_delta_min > 0 ? '+' : ''}{entry.streak_adjusted_delta_min}m
                </Badge>
              )}
            </div>
            <div className="mt-1.5 text-sm text-zinc-200 truncate">
              {entry.kind === 'pick_v2' || entry.kind === 'reminder_v2'
                ? (pickLabel(entry.pick_info) || <span className="text-zinc-500">(pick no encontrado)</span>)
                : entry.kind === 'community_text_v2' && entry.text
                  ? <span className="text-zinc-300">{String(entry.text).slice(0, 120)}</span>
                  : entry.kind === 'greeting_v2'
                    ? <span className="text-zinc-400 italic">Saludo {entry.greeting_slot || ''}</span>
                    : entry.kind === 'daily_balance_v2'
                      ? <span className="text-zinc-400 italic">Balance diario</span>
                      : entry.kind === 'filler_question_v2'
                        ? <span className="text-zinc-400 italic">Pregunta de engagement</span>
                        : <span className="text-zinc-500 italic">—</span>}
            </div>
            <div className="mt-1 flex items-center gap-3 text-[11px] text-zinc-500">
              <span>{formatISOHuman(entry.scheduled_for)}</span>
              {entry.created_by && (
                <span className="text-zinc-600">src: {entry.created_by}</span>
              )}
            </div>
          </div>
          {isPending && (
            <Button
              data-testid={`cancel-${entry.id}`}
              size="sm"
              variant="ghost"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0"
              onClick={() => onCancel(entry)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatsBar({ entries }) {
  const stats = useMemo(() => {
    const pending = entries.filter((e) => e.status === 'pending');
    const published = entries.filter((e) => e.status === 'published');
    const failed = entries.filter((e) => e.status === 'failed');
    const byKind = {};
    pending.forEach((e) => { byKind[e.kind] = (byKind[e.kind] || 0) + 1; });
    const nextUp = pending.slice(0, 1)[0];
    return { pending: pending.length, published: published.length, failed: failed.length, byKind, nextUp };
  }, [entries]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="queue-stats">
      <Card className="bg-zinc-900/60 border-white/5">
        <CardContent className="py-4">
          <div className="text-xs text-zinc-400 flex items-center gap-2">
            <Hourglass className="w-3.5 h-3.5" /> EN COLA
          </div>
          <div className="text-3xl font-bold text-amber-300 mt-1">{stats.pending}</div>
        </CardContent>
      </Card>
      <Card className="bg-zinc-900/60 border-white/5">
        <CardContent className="py-4">
          <div className="text-xs text-zinc-400 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> ENVIADOS
          </div>
          <div className="text-3xl font-bold text-emerald-400 mt-1">{stats.published}</div>
        </CardContent>
      </Card>
      <Card className="bg-zinc-900/60 border-white/5">
        <CardContent className="py-4">
          <div className="text-xs text-zinc-400 flex items-center gap-2">
            <XCircle className="w-3.5 h-3.5" /> FALLIDOS
          </div>
          <div className="text-3xl font-bold text-red-400 mt-1">{stats.failed}</div>
        </CardContent>
      </Card>
      <Card className="bg-zinc-900/60 border-white/5">
        <CardContent className="py-4">
          <div className="text-xs text-zinc-400 flex items-center gap-2">
            <ArrowRight className="w-3.5 h-3.5" /> PRÓXIMO
          </div>
          <div className="text-lg font-semibold text-zinc-100 mt-1 truncate">
            {stats.nextUp ? formatCountdown(minutesUntil(stats.nextUp.scheduled_for)) : '—'}
          </div>
          {stats.nextUp && (
            <div className="text-[11px] text-zinc-500 truncate">
              {(KIND_META[stats.nextUp.kind] || {}).label || stats.nextUp.kind} · {stats.nextUp.channel_target}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminTelegram() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterChannel, setFilterChannel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/v2/scheduled`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(`Error cargando cola: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 20s
  useEffect(() => {
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [load]);

  // Countdown re-render every 30s
  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  const filtered = useMemo(() => {
    let arr = entries;
    if (filterChannel !== 'all') arr = arr.filter((e) => e.channel_target === filterChannel);
    if (filterStatus !== 'all') arr = arr.filter((e) => e.status === filterStatus);
    return [...arr].sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for));
  }, [entries, filterChannel, filterStatus]);

  const cancelAll = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/telegram/v2/scheduled`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(`${data.cancelled} entradas canceladas`);
      await load();
    } catch (e) {
      toast.error(`Cancel all falló: ${e.message}`);
    }
  }, [load]);

  const cancelOne = useCallback(async (entry) => {
    setConfirmCancel(null);
    try {
      // There's no per-id cancel endpoint yet; delete directly in DB via admin endpoint.
      // Fallback: call DELETE /scheduled (cancels ALL) — we instead route to new per-id endpoint
      const res = await fetch(`${API_URL}/api/telegram/v2/scheduled/${entry.id}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Entrada cancelada`);
      await load();
    } catch (e) {
      toast.error(`Cancel falló: ${e.message}`);
    }
  }, [load]);

  const freshStart = useCallback(async ({ wipe }) => {
    setConfirmWipe(false);
    const body = {
      channels: ['free', 'vip'],
      wipe: wipe,
      include_picks: true,
      max_free: 4, max_vip: 4,
      pick_interval_min: 12,
      reminder_lead_min: 30,
    };
    try {
      const res = await fetch(`${API_URL}/api/telegram/v2/fresh-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const msgParts = [];
      if (wipe) {
        const f = data.channels?.free?.wiped?.deleted ?? 0;
        const v = data.channels?.vip?.wiped?.deleted ?? 0;
        msgParts.push(`Borrados FREE:${f} VIP:${v}`);
      }
      const fs = data.picks_scheduled?.free?.scheduled ?? 0;
      const vs = data.picks_scheduled?.vip?.scheduled ?? 0;
      msgParts.push(`Picks encolados F:${fs} V:${vs}`);
      toast.success(`Fresh start OK — ${msgParts.join(' · ')}`);
      await load();
    } catch (e) {
      toast.error(`Fresh start falló: ${e.message}`);
    }
  }, [load]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="admin-telegram-page">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Send className="w-6 h-6 text-emerald-400" /> Cola Telegram v2
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              Picks, recordatorios, saludos, balances y mensajes de comunidad encolados.
              Auto-refresh cada 20s.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="refresh-queue-btn"
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="border-white/10"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-1">Refrescar</span>
            </Button>
            <Button
              data-testid="fresh-start-btn"
              variant="outline"
              size="sm"
              onClick={() => setConfirmWipe(true)}
              className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
            >
              <Sparkles className="w-4 h-4 mr-1" /> Fresh Start
            </Button>
            <Button
              data-testid="cancel-all-btn"
              variant="outline"
              size="sm"
              onClick={cancelAll}
              className="border-red-500/30 text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Cancelar todo
            </Button>
          </div>
        </header>

        <StatsBar entries={entries} />

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-zinc-500 uppercase tracking-wider">Canal</span>
          {['all', 'free', 'vip'].map((ch) => (
            <Button
              key={ch}
              size="sm"
              data-testid={`filter-channel-${ch}`}
              variant={filterChannel === ch ? 'default' : 'outline'}
              onClick={() => setFilterChannel(ch)}
              className={filterChannel === ch ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-white/10'}
            >
              {ch === 'all' ? 'Todos' : ch.toUpperCase()}
            </Button>
          ))}
          <span className="text-xs text-zinc-500 uppercase tracking-wider ml-4">Estado</span>
          {['pending', 'published', 'all'].map((s) => (
            <Button
              key={s}
              size="sm"
              data-testid={`filter-status-${s}`}
              variant={filterStatus === s ? 'default' : 'outline'}
              onClick={() => setFilterStatus(s)}
              className={filterStatus === s ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-white/10'}
            >
              {s === 'all' ? 'Todos' : (STATUS_META[s]?.label || s)}
            </Button>
          ))}
        </div>

        <div className="space-y-2" data-testid="queue-list">
          {filtered.length === 0 ? (
            <Card className="bg-zinc-900/40 border-white/5">
              <CardContent className="py-12 text-center text-zinc-500">
                <Coffee className="w-10 h-10 mx-auto mb-3 opacity-40" />
                {loading ? 'Cargando…' : 'No hay entradas en esta vista.'}
              </CardContent>
            </Card>
          ) : filtered.map((e) => (
            <QueueEntry key={e.id} entry={e} onCancel={setConfirmCancel} />
          ))}
        </div>
      </main>

      {/* Confirm cancel single */}
      <Dialog open={!!confirmCancel} onOpenChange={(o) => !o && setConfirmCancel(null)}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" /> Cancelar entrada
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-300">
            Esta entrada no se enviará al canal. La acción no se puede deshacer.
          </p>
          {confirmCancel && (
            <div className="text-xs text-zinc-500 p-3 bg-zinc-900/60 rounded border border-white/5">
              <div><b>Tipo:</b> {(KIND_META[confirmCancel.kind] || {}).label || confirmCancel.kind}</div>
              <div><b>Canal:</b> {confirmCancel.channel_target}</div>
              <div><b>Programado:</b> {formatISOHuman(confirmCancel.scheduled_for)}</div>
              {confirmCancel.pick_info && (
                <div className="mt-1"><b>Pick:</b> {pickLabel(confirmCancel.pick_info)}</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(null)}>Volver</Button>
            <Button
              variant="destructive"
              data-testid="confirm-cancel-btn"
              onClick={() => cancelOne(confirmCancel)}
            >
              Cancelar envío
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm fresh start */}
      <Dialog open={confirmWipe} onOpenChange={setConfirmWipe}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" /> Fresh Start Telegram
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-zinc-300">
            <p>
              Arranca los canales desde cero. Esto:
            </p>
            <ul className="list-disc pl-5 text-zinc-400 space-y-1">
              <li><b>Borrará</b> todos los mensajes previos del bot en FREE y VIP (si eliges).</li>
              <li>Cancelará entradas pendientes en la cola.</li>
              <li>Publicará un saludo inicial (GM / GA / GN según la hora).</li>
              <li>Programará saludos futuros del día + 1 pregunta de engagement.</li>
              <li>Encolará los picks de hoy con pacing (12 min entre picks, recordatorios 30 min antes).</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmWipe(false)}>Volver</Button>
            <Button
              data-testid="fresh-start-no-wipe-btn"
              variant="outline"
              className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
              onClick={() => freshStart({ wipe: false })}
            >
              Sólo planificar (no borrar)
            </Button>
            <Button
              data-testid="fresh-start-wipe-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => freshStart({ wipe: true })}
            >
              <Trash2 className="w-4 h-4 mr-1" /> Borrar y arrancar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
